<template>
  <b-row>

    <b-col>
      <b-card :title="cardTitle">
        <template
          v-if="patientItems.length"
          class="rounded"
        >
          <template v-if="hasMultiplePatients">
            <div
              v-for="patient in patientItems"
              :key="patient.key"
              class=""
            >
              <b-button
                block
                :variant="patientToggleVariant(patient.collapseId)"
                class="text-left d-flex justify-content-between align-items-center rounded mb-50"
                :aria-controls="patient.collapseId"
                @click="setActive(patient.collapseId)"
              >
                <span>{{ patient.label }}</span>
              </b-button>
              <b-collapse
                :id="patient.collapseId"
                :visible="isActive(patient.collapseId)"
              >
                <div class="px-75 pb-75 pt-75">
                  <dl
                    v-for="param in patient.infoList"
                    :key="`${patient.collapseId}-${param.label}`"
                    class="row mb-0"
                  >
                    <dt class="font-bold col-4">
                      {{ param.label }}
                    </dt>
                    <dd class="col-8">
                      {{ param.value }} {{ param.postfix }}
                    </dd>
                  </dl>
                  <template v-if="patient.caregiverInfoList.length">
                    <h5 class="mt-75 mb-50 ml-n1 text-uppercase text-muted">
                      {{ $t('order.patient-info.caregiver.title') }}
                    </h5>
                    <dl
                      v-for="param in patient.caregiverInfoList"
                      :key="`${patient.collapseId}-caregiver-${param.label}`"
                      class="row mb-0"
                    >
                      <dt class="font-bold col-4">
                        {{ param.label }}
                      </dt>
                      <dd class="col-8">
                        {{ param.value }} {{ param.postfix }}
                      </dd>
                    </dl>
                  </template>
                </div>
              </b-collapse>
            </div>
          </template>
          <div
            v-else
            class="px-75 pt-75"
          >
            <dl
              v-for="param in patientItems[0].infoList"
              :key="`order-patient-info-single-${param.label}`"
              class="row mb-0"
            >
              <dt class="font-bold col-4">
                {{ param.label }}
              </dt>
              <dd class="col-8">
                {{ param.value }} {{ param.postfix }}
              </dd>
            </dl>
            <template v-if="patientItems[0].caregiverInfoList.length">
              <h5 class="mt-75 mb-50 ml-n1 text-uppercase text-muted">
                {{ $t('order.patient-info.caregiver.title') }}
              </h5>
              <dl
                v-for="param in patientItems[0].caregiverInfoList"
                :key="`order-patient-info-single-caregiver-${param.label}`"
                class="row mb-0"
              >
                <dt class="font-bold col-4">
                  {{ param.label }}
                </dt>
                <dd class="col-8">
                  {{ param.value }} {{ param.postfix }}
                </dd>
              </dl>
            </template>
          </div>
        </template>
        <p
          v-else
          class="mb-1 text-muted"
        >
          {{ $t('order.patient-info.list.empty') }}
        </p>

        <b-button
          v-if="allowAddPatient"
          class="btn-block mb-50"
          variant="flat-primary"
          @click="openAddPatientModal"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-25"
          />
          {{ $t('order.patient-info.list.add') }}
        </b-button>
      </b-card>
      <add-patient-modal
        ref="addPatientModal"
        :can-add-patient="allowAddPatient"
      />
    </b-col>

  </b-row>
</template>
<script>
import AddPatientModal from '@/views/orders/partials/AddPatientModal.vue'
import { getPatientCaregiver, isPatientVisibleInView, shouldDisplayPatientCaregiver } from '@/helpers/patient'
import useAppConfig from '@core/app-config/useAppConfig'
import { getPatientToggleVariant } from './helpers/patientInfoShort'

export default {
  name: 'OrderPatientInfoShort',
  components: {
    AddPatientModal,
  },
  props: {
    allowAddPatient: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeCollapseId: null,
    }
  },
  computed: {
    isDark() {
      return useAppConfig().skin.value === 'dark'
    },
    order() {
      return this.$store.getters['order/getOrderData']
    },
    patients() {
      const patients = Array.isArray(this.order.patientInfo) ? this.order.patientInfo : []
      return patients.filter(patient => isPatientVisibleInView(patient))
    },
    patientItems() {
      return this.patients.map((patient, index) => ({
        key: patient?.id ?? index,
        label: this.getPatientLabel(patient, index),
        infoList: this.buildPatientInfoList(patient),
        caregiverInfoList: this.buildCaregiverInfoList(patient),
        collapseId: `order-patient-info-short-${index}`,
      }))
    },
    hasMultiplePatients() {
      return this.patientItems.length > 1
    },
    cardTitle() {
      const titleKey = this.hasMultiplePatients
        ? 'order.patient-info.title-multiple'
        : 'order.patient-info.title'

      return this.$t(titleKey)
    },
  },
  watch: {
    patientItems: {
      immediate: true,
      handler(items) {
        if (items.length <= 1) {
          this.activeCollapseId = null
          return
        }
        const firstCollapseId = items[0]?.collapseId ?? null
        const currentExists = items.some(item => item.collapseId === this.activeCollapseId)
        if (!currentExists) {
          this.activeCollapseId = firstCollapseId
        }
      },
    },
  },
  methods: {
    openAddPatientModal() {
      if (!this.allowAddPatient) {
        return
      }
      const modal = this.$refs.addPatientModal
      if (modal && typeof modal.open === 'function') {
        modal.open()
      }
    },
    isActive(collapseId) {
      return this.activeCollapseId === collapseId
    },
    setActive(collapseId) {
      if (!this.hasMultiplePatients) {
        return
      }
      this.activeCollapseId = collapseId
    },
    patientToggleVariant(collapseId) {
      return getPatientToggleVariant({
        isActive: this.isActive(collapseId),
        isDark: this.isDark,
      })
    },
    getPatientLabel(patient, index) {
      const labelParts = [patient?.surname, patient?.name].filter(Boolean)
      if (labelParts.length) {
        return labelParts.join(' ')
      }
      return this.$t('order.patient-info.single-title', {
        index: index + 1,
      })
    },
    buildPatientInfoList(patient = {}) {
      const list = []

      if (patient.surname) {
        list.push({
          label: this.$t('order.patient-info.surname'),
          value: patient.surname,
          postfix: null,
        })
      }

      if (patient.name) {
        list.push({
          label: this.$t('order.patient-info.name'),
          value: patient.name,
          postfix: null,
        })
      }

      if (patient.age) {
        list.push({
          label: this.$t('order.patient-info.age'),
          value: patient.age,
          postfix: this.$t('order.patient-info.years'),
        })
      }

      if (patient.sex) {
        list.push({
          label: this.$t('order.patient-info.sex'),
          value: this.$t(`order.patient-info.${patient.sex}`),
          postfix: null,
        })
      }

      if (patient.weight) {
        list.push({
          label: this.$t('order.patient-info.weight'),
          value: patient.weight,
          postfix: 'kg',
        })
      }

      if (patient.pesel) {
        list.push({
          label: this.$t('order.patient-info.pesel'),
          value: patient.pesel,
          postfix: null,
        })
      }

      return list
    },
    buildCaregiverInfoList(patient = {}) {
      if (!shouldDisplayPatientCaregiver(patient)) {
        return []
      }

      const caregiver = getPatientCaregiver(patient)
      const list = []

      if (caregiver.name) {
        list.push({
          label: this.$t('order.patient-info.caregiver.name'),
          value: caregiver.name,
          postfix: null,
        })
      }

      if (caregiver.surname) {
        list.push({
          label: this.$t('order.patient-info.caregiver.surname'),
          value: caregiver.surname,
          postfix: null,
        })
      }

      if (caregiver.pesel) {
        list.push({
          label: this.$t('order.patient-info.caregiver.pesel'),
          value: caregiver.pesel,
          postfix: null,
        })
      }

      if (caregiver.phone) {
        list.push({
          label: this.$t('order.patient-info.caregiver.phone'),
          value: caregiver.phone,
          postfix: null,
        })
      }

      return list
    },
  },
}
</script>
